import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useMemberAuth } from '../../../contexts/MemberAuthContext';
import { API_BASE_URL } from '../../../utils/apiConfig';
import ProfilePhotoUpload from '../../../components/ProfilePhotoUpload';
import { mfetch, MemberApiError } from '../api';
import Icon from '../design/Icon';
import { GympePill, Sheet, Toggle, fmtDateShort } from '../design/atoms';
import type { MemberGym } from '../hooks/useMemberGyms';

/* SPEC 11 Profile — avatar · membership card · settings lists · danger row ·
   log out · Gympe pill footer. */

interface OutletCtx {
  gyms: MemberGym[];
  activeGym: MemberGym | null;
  activeGymId: string | null;
  setActiveGymId: (id: string | null) => void;
  hasClassScheduling: boolean;
  loading: boolean;
}

interface BillingMembership {
  status: string | null;
  startDate: string | null;
  endDate: string | null;
  plan: { name: string } | null;
}

interface HealthData {
  gender: string;
  dateOfBirth: string;
  heightCm: string;
  currentWeightKg: string;
  targetWeightKg: string;
  activityLevel: string;
  experienceLevel: string;
  injuries: string;
  medicalConditions: string[];
}

const ACTIVITY_LEVELS = [
  { value: 'sedentary', label: 'Sedentary (office job)' },
  { value: 'light', label: 'Lightly active' },
  { value: 'moderate', label: 'Moderately active' },
  { value: 'active', label: 'Very active' },
  { value: 'extreme', label: 'Extra active (athlete)' },
];

const EXPERIENCE_LEVELS = [
  { value: 'beginner', label: 'Beginner (< 1 yr)' },
  { value: 'intermediate', label: 'Intermediate (1-3 yrs)' },
  { value: 'advanced', label: 'Advanced (3+ yrs)' },
];

const EMPTY_HEALTH: HealthData = {
  gender: '',
  dateOfBirth: '',
  heightCm: '',
  currentWeightKg: '',
  targetWeightKg: '',
  activityLevel: 'moderate',
  experienceLevel: 'beginner',
  injuries: '',
  medicalConditions: [],
};

function isoToYmd(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function MemberProfilePage() {
  const { member, token, refreshMember, signOut, updateProfilePhoto } = useMemberAuth();
  const { gyms, activeGymId, setActiveGymId, loading: gymsLoading } = useOutletContext<OutletCtx>();
  const navigate = useNavigate();

  const [membership, setMembership] = useState<BillingMembership | null>(null);
  const [membershipLoading, setMembershipLoading] = useState(true);
  const [health, setHealth] = useState<HealthData>(EMPTY_HEALTH);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [openSheet, setOpenSheet] = useState<'details' | 'health' | 'gyms' | null>(null);
  // Notification preferences (ON = subscribed). Applies across all the member's gyms.
  const [notifPrefs, setNotifPrefs] = useState({ transactional: true, marketing: true });
  const [notifSaving, setNotifSaving] = useState(false);

  // Active membership for the near-black card (best effort).
  useEffect(() => {
    if (!token || !activeGymId) {
      setMembershipLoading(false);
      return;
    }
    setMembershipLoading(true);
    mfetch(token, '/member/billing', { params: { gymId: activeGymId } })
      .then(r => {
        const items: BillingMembership[] = r.data ?? [];
        const now = new Date();
        const active = items.find(
          m => (m.status ?? '').toLowerCase() === 'active' && m.endDate && new Date(m.endDate) >= now
        );
        setMembership(active ?? null);
      })
      .catch(() => setMembership(null))
      .finally(() => setMembershipLoading(false));
  }, [token, activeGymId]);

  // Health profile (loaded once; edited in the sheet).
  useEffect(() => {
    if (!token) return;
    mfetch(token, '/member/health-profile')
      .then(r => {
        const d = r.data;
        if (!d) return;
        setHealth({
          gender: d.gender || '',
          dateOfBirth: d.dateOfBirth ? d.dateOfBirth.split('T')[0] : '',
          heightCm: String(d.heightCm || ''),
          currentWeightKg: String(d.currentWeightKg || ''),
          targetWeightKg: String(d.targetWeightKg || ''),
          activityLevel: d.activityLevel || 'moderate',
          experienceLevel: d.experienceLevel || 'beginner',
          injuries: d.injuries || '',
          medicalConditions: d.medicalConditions || [],
        });
      })
      .catch(() => { /* keep defaults; sheet still works */ });
  }, [token]);

  // Notification preferences (WhatsApp consent, across all gyms)
  useEffect(() => {
    if (!token) return;
    mfetch(token, '/member/notification-preferences')
      .then(r => {
        if (r?.data) {
          setNotifPrefs({
            transactional: r.data.transactional ?? true,
            marketing: r.data.marketing ?? true,
          });
        }
      })
      .catch(() => { /* defaults stay on */ });
  }, [token]);

  const saveNotifPrefs = async (next: { transactional: boolean; marketing: boolean }) => {
    if (!token) return;
    const prev = notifPrefs;
    setNotifPrefs(next);
    setNotifSaving(true);
    try {
      await mfetch(token, '/member/notification-preferences', { method: 'PATCH', body: next });
    } catch {
      setNotifPrefs(prev);
      toast.error('Could not update notification preferences');
    } finally {
      setNotifSaving(false);
    }
  };


  const handlePhotoUpload = async (blob: Blob) => {
    if (!token) return;
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('photo', blob, 'profile.jpg');
      formData.append('photoType', 'profile');
      const res = await fetch(`${API_BASE_URL}/app/member/progress/photos`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      if (data.profilePhoto) updateProfilePhoto(data.profilePhoto);
      refreshMember();
      toast.success('Profile photo updated');
    } catch (err: any) {
      toast.error(err.message || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/app/login');
  };

  const name = member?.displayName || member?.fullName || 'Member';

  return (
    <div className="mpage">
      {/* Identity */}
      <div className="col" style={{ alignItems: 'center', gap: 8, paddingTop: 14 }}>
        <ProfilePhotoUpload
          currentPhotoUrl={member?.memberProfilePhoto ?? member?.profilePhoto ?? null}
          fallbackPhotoUrl={member?.gymProfilePhoto ?? null}
          onUpload={handlePhotoUpload}
          uploading={uploadingPhoto}
          initials={name.charAt(0).toUpperCase()}
          size={72}
          variant="light"
        />
        <div className="col" style={{ alignItems: 'center', gap: 2 }}>
          <div className="dp dp-m">{name}</div>
          <div className="sub">{member?.phoneNumber}</div>
        </div>
      </div>

      {/* Membership card → billing */}
      {membershipLoading ? (
        <div className="skel" style={{ height: 96, borderRadius: 'var(--r-m)' }} />
      ) : (
        <button className="card ink" style={{ textAlign: 'left', width: '100%' }} onClick={() => navigate('/app/billing')}>
          <div className="between">
            <div className="col" style={{ gap: 6, minWidth: 0 }}>
              <div className="lbl">Membership</div>
              {membership ? (
                <>
                  <div className="dp dp-m trunc" style={{ color: 'var(--on-ink)' }}>{membership.plan?.name ?? 'Membership'}</div>
                  {membership.endDate && (
                    <div className="sub">Expires {fmtDateShort(isoToYmd(membership.endDate))}</div>
                  )}
                </>
              ) : (
                <>
                  <div className="dp dp-m" style={{ color: 'var(--on-ink)' }}>No active membership</div>
                  <div className="sub wrap">
                    {activeGymId ? 'Talk to the front desk to pick a plan.' : 'Join a gym to get started.'}
                  </div>
                </>
              )}
            </div>
            <div className="col" style={{ alignItems: 'flex-end', gap: 10 }}>
              {membership && <span className="tag sage">Active</span>}
              <Icon n="right" s={16} c="color-mix(in srgb, var(--on-ink) 55%, transparent)" />
            </div>
          </div>
        </button>
      )}

      {/* Settings list 1 */}
      <div className="card" style={{ padding: '5px 16px' }}>
        <button className="lrow" onClick={() => setOpenSheet('details')}>
          <div className="li gold"><Icon n="user" s={16} c="var(--gold-2)" /></div>
          <div className="col"><div className="mid">Personal details</div></div>
          <Icon n="right" s={16} c="var(--ink-3)" />
        </button>
        <button className="lrow" onClick={() => setOpenSheet('health')}>
          <div className="li gold"><Icon n="heart" s={16} c="var(--gold-2)" /></div>
          <div className="col" style={{ gap: 1 }}>
            <div className="mid">Health & injuries</div>
            {health.injuries && <div className="tiny trunc">{health.injuries}</div>}
          </div>
          <Icon n="right" s={16} c="var(--ink-3)" />
        </button>
      </div>

      {/* Settings list 2 */}
      <div className="card" style={{ padding: '5px 16px' }}>
        <button className="lrow" onClick={() => navigate('/app/checkins')}>
          <div className="li"><Icon n="clock" s={16} c="var(--ink-2)" /></div>
          <div className="col"><div className="mid">Check-in history</div></div>
          <Icon n="right" s={16} c="var(--ink-3)" />
        </button>
        <button className="lrow" onClick={() => navigate('/app/billing')}>
          <div className="li"><Icon n="card" s={16} c="var(--ink-2)" /></div>
          <div className="col"><div className="mid">Billing & payments</div></div>
          <Icon n="right" s={16} c="var(--ink-3)" />
        </button>
        <button className="lrow" onClick={() => navigate('/app/pauses')}>
          <div className="li"><Icon n="moon" s={16} c="var(--ink-2)" /></div>
          <div className="col" style={{ gap: 1 }}>
            <div className="mid">Pause plan or pack</div>
            <div className="tiny">Away for a while? Your days wait for you</div>
          </div>
          <Icon n="right" s={16} c="var(--ink-3)" />
        </button>
        {!gymsLoading && gyms.length > 1 && (
          <button className="lrow" onClick={() => setOpenSheet('gyms')}>
            <div className="li"><Icon n="pin" s={16} c="var(--ink-2)" /></div>
            <div className="col"><div className="mid">Switch gym</div></div>
            <Icon n="right" s={16} c="var(--ink-3)" />
          </button>
        )}
      </div>

      {/* Notifications (WhatsApp consent) */}
      <div className="card" style={{ padding: '5px 16px' }}>
        <div className="lrow" style={{ cursor: 'default' }}>
          <div className="li"><Icon n="bell" s={16} c="var(--ink-2)" /></div>
          <div className="col" style={{ gap: 1 }}>
            <div className="mid">Booking & payment updates</div>
            <div className="tiny">Receipts, reminders, bookings</div>
          </div>
          <Toggle on={notifPrefs.transactional} onChange={v => !notifSaving && saveNotifPrefs({ ...notifPrefs, transactional: v })} />
        </div>
        <div className="lrow" style={{ cursor: 'default' }}>
          <div className="li"><Icon n="gift" s={16} c="var(--ink-2)" /></div>
          <div className="col" style={{ gap: 1 }}>
            <div className="mid">Offers & announcements</div>
            <div className="tiny">Promotions, broadcasts, birthday wishes</div>
          </div>
          <Toggle on={notifPrefs.marketing} onChange={v => !notifSaving && saveNotifPrefs({ ...notifPrefs, marketing: v })} />
        </div>
      </div>

      {/* Danger */}
      <div className="card flat" style={{ padding: '5px 16px' }}>
        <button className="lrow" onClick={() => navigate('/app/profile/delete')}>
          <div className="li"><Icon n="trash" s={16} c="var(--clay)" /></div>
          <div className="col"><div className="mid" style={{ color: 'var(--clay)' }}>Delete account & data</div></div>
          <Icon n="right" s={16} c="var(--ink-3)" />
        </button>
      </div>

      <button className="btn ghost block" onClick={handleLogout}>
        <Icon n="out" s={17} c="var(--ink)" />
        Log out
      </button>

      <div className="col" style={{ alignItems: 'center', gap: 6, paddingTop: 4 }}>
        <GympePill />

      </div>

      {openSheet === 'details' && token && (
        <DetailsSheet
          token={token}
          initial={{
            fullName: member?.fullName || '',
            displayName: member?.displayName || '',
            email: member?.email || '',
          }}
          phoneNumber={member?.phoneNumber || ''}
          onSaved={async () => {
            await refreshMember();
            setOpenSheet(null);
          }}
          onClose={() => setOpenSheet(null)}
        />
      )}

      {openSheet === 'health' && token && (
        <HealthSheet
          token={token}
          initial={health}
          onSaved={h => {
            setHealth(h);
            setOpenSheet(null);
          }}
          onClose={() => setOpenSheet(null)}
        />
      )}

      {openSheet === 'gyms' && (
        <Sheet onClose={() => setOpenSheet(null)}>
          <div className="dp dp-m">Your gyms</div>
          <div className="col">
            {gyms.map(g => (
              <button
                key={g.id}
                className="lrow"
                onClick={() => {
                  setActiveGymId(g.id);
                  setOpenSheet(null);
                }}
              >
                <div className="mark" style={{ width: 34, height: 34, flex: '0 0 34px', fontSize: 16, borderRadius: 11 }}>
                  {g.logoUrl ? <img src={g.logoUrl} alt="" /> : (g.displayName || g.name).charAt(0).toUpperCase()}
                </div>
                <div className="col" style={{ gap: 1 }}>
                  <div className="mid trunc">{g.displayName || g.name}</div>
                </div>
                {g.id === activeGymId && <Icon n="check" s={18} c="var(--gold)" />}
              </button>
            ))}
          </div>
        </Sheet>
      )}
    </div>
  );
}

// ── Personal details sheet (PATCH /app/member/profile) ──────────────────────
function DetailsSheet({
  token,
  initial,
  phoneNumber,
  onSaved,
  onClose,
}: {
  token: string;
  initial: { fullName: string; displayName: string; email: string };
  phoneNumber: string;
  onSaved: () => Promise<void>;
  onClose: () => void;
}) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await mfetch(token, '/member/profile', { method: 'PATCH', body: form });
      await onSaved();
    } catch (e) {
      setSaving(false);
      setError(e instanceof MemberApiError ? e.message : 'Could not save your details');
    }
  };

  return (
    <Sheet onClose={saving ? () => {} : onClose}>
      <div className="between">
        <div className="dp dp-m">Personal details</div>
        <button className="icon-btn" aria-label="Close" onClick={onClose}>
          <Icon n="x" s={16} c="var(--ink-2)" />
        </button>
      </div>

      <div className="col" style={{ gap: 10 }}>
        <div className="col" style={{ gap: 5 }}>
          <span className="lbl">Full name</span>
          <input
            className="minput"
            value={form.fullName}
            onChange={e => setForm({ ...form, fullName: e.target.value })}
            placeholder="Your legal name"
          />
        </div>
        <div className="col" style={{ gap: 5 }}>
          <span className="lbl">Display name</span>
          <input
            className="minput"
            value={form.displayName}
            onChange={e => setForm({ ...form, displayName: e.target.value })}
            placeholder="How you appear in the gym"
          />
        </div>
        <div className="col" style={{ gap: 5 }}>
          <span className="lbl">Email</span>
          <input
            className="minput"
            type="email"
            value={form.email}
            onChange={e => setForm({ ...form, email: e.target.value })}
            placeholder="you@example.com"
          />
        </div>
        <div className="field">
          <span className="lbl">Phone</span>
          <b>{phoneNumber || '—'}</b>
        </div>
      </div>

      {error && (
        <div className="card flat" style={{ padding: '10px 12px', borderColor: 'var(--clay)', background: 'var(--clay-soft)' }}>
          <span className="sub wrap" style={{ color: 'var(--clay)' }}>{error}</span>
        </div>
      )}

      <button className="btn gold block" disabled={saving} onClick={save}>
        {saving ? 'Saving…' : 'Save'}
      </button>
    </Sheet>
  );
}

// ── Health & injuries sheet (PATCH /app/member/health-profile) ──────────────
function HealthSheet({
  token,
  initial,
  onSaved,
  onClose,
}: {
  token: string;
  initial: HealthData;
  onSaved: (h: HealthData) => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState<HealthData>(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = useCallback(<K extends keyof HealthData>(key: K, value: HealthData[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
  }, []);

  const save = async () => {
    setError(null);
    // Client-side validation kept from the previous implementation.
    if (form.dateOfBirth) {
      const birthDate = new Date(form.dateOfBirth);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
      if (age < 18) {
        setError('You must be at least 18 years old');
        return;
      }
    }
    if (Number(form.heightCm) < 0 || Number(form.currentWeightKg) < 0 || Number(form.targetWeightKg) < 0) {
      setError('Height and weight cannot be negative');
      return;
    }

    setSaving(true);
    try {
      await mfetch(token, '/member/health-profile', {
        method: 'PATCH',
        body: {
          ...form,
          heightCm: form.heightCm ? Number(form.heightCm) : null,
          currentWeightKg: form.currentWeightKg ? Number(form.currentWeightKg) : null,
          targetWeightKg: form.targetWeightKg ? Number(form.targetWeightKg) : null,
        },
      });
      onSaved(form);
    } catch (e) {
      setSaving(false);
      setError(e instanceof MemberApiError ? e.message : 'Could not save your health profile');
    }
  };

  return (
    <Sheet onClose={saving ? () => {} : onClose}>
      <div className="between">
        <div className="dp dp-m">Health & injuries</div>
        <button className="icon-btn" aria-label="Close" onClick={onClose}>
          <Icon n="x" s={16} c="var(--ink-2)" />
        </button>
      </div>

      <div className="grid2">
        <div className="col" style={{ gap: 5 }}>
          <span className="lbl">Gender</span>
          <select className="minput" value={form.gender} onChange={e => set('gender', e.target.value)}>
            <option value="">Select…</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
            <option value="private">Prefer not to say</option>
          </select>
        </div>
        <div className="col" style={{ gap: 5 }}>
          <span className="lbl">Date of birth</span>
          <input className="minput" type="date" value={form.dateOfBirth} onChange={e => set('dateOfBirth', e.target.value)} />
        </div>
        <div className="col" style={{ gap: 5 }}>
          <span className="lbl">Height (cm)</span>
          <input className="minput" type="number" value={form.heightCm} onChange={e => set('heightCm', e.target.value)} placeholder="175" />
        </div>
        <div className="col" style={{ gap: 5 }}>
          <span className="lbl">Weight (kg)</span>
          <input className="minput" type="number" value={form.currentWeightKg} onChange={e => set('currentWeightKg', e.target.value)} placeholder="75" />
        </div>
        <div className="col" style={{ gap: 5 }}>
          <span className="lbl">Target weight (kg)</span>
          <input className="minput" type="number" value={form.targetWeightKg} onChange={e => set('targetWeightKg', e.target.value)} placeholder="70" />
        </div>
        <div className="col" style={{ gap: 5 }}>
          <span className="lbl">Experience</span>
          <select className="minput" value={form.experienceLevel} onChange={e => set('experienceLevel', e.target.value)}>
            {EXPERIENCE_LEVELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
          </select>
        </div>
      </div>

      <div className="col" style={{ gap: 5 }}>
        <span className="lbl">Activity level</span>
        <select className="minput" value={form.activityLevel} onChange={e => set('activityLevel', e.target.value)}>
          {ACTIVITY_LEVELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
        </select>
      </div>

      <div className="col" style={{ gap: 5 }}>
        <span className="lbl">Injuries — noted for coaches</span>
        <textarea
          className="minput"
          style={{ minHeight: 74, resize: 'vertical' }}
          value={form.injuries}
          onChange={e => set('injuries', e.target.value)}
          placeholder="Any back pain, knee issues, etc."
        />
      </div>

      {error && (
        <div className="card flat" style={{ padding: '10px 12px', borderColor: 'var(--clay)', background: 'var(--clay-soft)' }}>
          <span className="sub wrap" style={{ color: 'var(--clay)' }}>{error}</span>
        </div>
      )}

      <button className="btn gold block" disabled={saving} onClick={save}>
        {saving ? 'Saving…' : 'Save'}
      </button>
    </Sheet>
  );
}
